import { z } from "zod";

// TES-433 — the patient's express consent that the service starts immediately
// and that the right of withdrawal therefore lapses.
//
// Swedish distance-selling law (distansavtalslagen) does not treat this as a
// checkbox: for a service that begins inside the 14-day withdrawal window the
// consumer must give EXPRESS consent to the early start and acknowledge that
// the withdrawal right is lost. What matters afterwards is evidence — so the
// consent is recorded server-side against the exact purchase, in the audit log
// (which carries a no-delete trigger), not merely held in client state.
//
// The copy is the, verbatim, and lives in the app's locale
// bundles under `consultPayment.terms.*`. It is patient-facing Swedish and is
// not paraphrased here.

/**
 * Version of the withdrawal-rights consent text currently in force.
 *
 * Bump this whenever the wording changes. The date form is deliberate: the
 * value is stored next to each consent, so a later dispute resolves to "which
 * text did this patient actually see", and a plain date is the fastest way to
 * answer that. Client and server compare against this same constant, so an app
 * still showing superseded wording is rejected rather than silently recorded
 * as having consented to the current text.
 */
export const CONSULT_TERMS_VERSION = "2026-09-01";

/** Body of `POST /patient-api/v1/billing/consult-payment/terms-consent`. */
export const consultTermsConsentSchema = z.object({
  // What the client believes it displayed. Checked against
  // CONSULT_TERMS_VERSION rather than trusted — see the constant's note.
  termsVersion: z.string().min(1).max(40),
});

export type ConsultTermsConsent = z.infer<typeof consultTermsConsentSchema>;

// TES-433 (Ö-83) — the same consent, same wording and same
// version, in the WEB checkout that actually starts the subscription. Body of
// `POST /patient-api/v1/billing/checkout/terms-consent`.
export const subscriptionTermsConsentSchema = consultTermsConsentSchema;
export type SubscriptionTermsConsent = ConsultTermsConsent;

// TES-1140 — body of `POST /patient-api/v1/billing/checkout`. A promotion code
// replaces the free launch month instead of stacking on it. Empty = no code.
export const subscriptionCheckoutSchema = z.object({
  promotionCode: z.string().trim().max(64).optional(),
});
export type SubscriptionCheckout = z.infer<typeof subscriptionCheckoutSchema>;
